import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Study settings: upload stickers for each moment of a session, and write (or
// paste) the lines he says at each moment — one per line.

const MOMENT_LABELS = {
  study: 'while studying',
  done: 'session done',
  break: 'break time',
  back: 'when you come back',
};
const BUILT_IN = {
  study: ['/stickers/night.webp', '/stickers/idea.webp', '/stickers/peek.webp'],
  done: ['/stickers/pat.webp'],
  break: ['/stickers/feed.webp'],
  back: ['/stickers/grumpy.webp'],
};

// small line icons, drawn to match the rest of the study pages
const Sparkle = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3l1.9 5.6L19.5 10.5l-5.6 1.9L12 18l-1.9-5.6L4.5 10.5l5.6-1.9z" />
    <path d="M19 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z" />
  </svg>
);
const Eye = ({ open = true, size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
    <circle cx="12" cy="12" r="2.8" />
    {!open && <path d="M4 4l16 16" />}
  </svg>
);
const Heart = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 20.5s-7.5-4.6-9.3-9.2C1.4 7.9 3.6 4.5 7 4.5c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.4 0 5.6 3.4 4.3 6.8-1.8 4.6-9.3 9.2-9.3 9.2z" />
  </svg>
);
const Alert = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" style={{ verticalAlign: '-2px', marginRight: '6px' }}>
    <circle cx="12" cy="12" r="9" /><path d="M12 7.5v5.5M12 16.5v.01" />
  </svg>
);

export default function StudySettings() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [moments, setMoments] = useState([]);
  const [stickers, setStickers] = useState([]);
  const [uploading, setUploading] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');
  const [categories, setCategories] = useState([]);
  const [drafts, setDrafts] = useState({});         // category -> textarea text
  const [savingCat, setSavingCat] = useState('');
  const [suggesting, setSuggesting] = useState('');
  const [openCat, setOpenCat] = useState('');
  const [toast, setToast] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const fileRefs = useRef({});
  const [reading, setReading] = useState(null);     // { visible, balance_seconds, active }
  const [readingSaving, setReadingSaving] = useState(false);
  // study subjects: name, reading-time rate, colour, goal, whether he minds you leaving
  const [subjects, setSubjects] = useState(null);
  const [subjectsSaved, setSubjectsSaved] = useState('');
  const [palette, setPalette] = useState([]);
  const [savingSubjects, setSavingSubjects] = useState(false);

  useEffect(() => {
    if (!token) { navigate('/'); return; }
    loadStickers();
    loadLines();
    axios.get(`${API}/reading/state?token=${token}`).then(res => setReading(res.data)).catch(() => {});
    axios.get(`${API}/study/subjects?token=${token}`).then(res => {
      setSubjects(res.data.subjects || []); setSubjectsSaved(JSON.stringify(res.data.subjects || [])); setPalette(res.data.colors || []);
    }).catch(() => {});
  }, []);

  const describeError = (err) => {
    const d = err?.response?.data?.detail;
    return typeof d === 'string' ? d : (err?.message || 'something went wrong');
  };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2600); };

  const loadStickers = () => {
    axios.get(`${API}/study/stickers?token=${token}`)
      .then(res => { setMoments(res.data.moments || []); setStickers(res.data.stickers || []); })
      .catch(err => setErrorMsg(`couldn't load stickers: ${describeError(err)}`));
  };

  const loadLines = () => {
    axios.get(`${API}/study/lines?token=${token}`)
      .then(res => {
        const cats = res.data.categories || [];
        setCategories(cats);
        const d = {};
        cats.forEach(c => { d[c.key] = c.custom.join('\n'); });
        setDrafts(d);
      })
      .catch(err => setErrorMsg(`couldn't load messages: ${describeError(err)}`));
  };

  // ---- reading time: only shows or hides it — you keep earning either way ----
  const setReadingVisible = (visible) => {
    const wasReading = !!reading?.active;
    setReadingSaving(true);
    setReading(r => (r ? { ...r, visible } : r));
    axios.put(`${API}/reading/visibility`, { token, visible })
      .then(res => {
        setReading(res.data);
        showToast(visible ? 'reading time is showing again' : wasReading ? 'hidden — your reading session was saved' : 'reading time hidden — still earning');
      })
      .catch(err => { setErrorMsg(`couldn't change it: ${describeError(err)}`); setReading(r => (r ? { ...r, visible: !visible } : r)); })
      .finally(() => setReadingSaving(false));
  };
  const readingHm = (sec) => {
    const m = Math.ceil((sec || 0) / 60);      // rounded up, same as the study page
    const h = Math.floor(m / 60);
    return h ? (m % 60 ? `${h}h ${m % 60}m` : `${h}h`) : `${m}m`;
  };

  // ---- subjects ----
  const editSubject = (i, patch) => setSubjects(list => list.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addSubject = () => setSubjects(list => {
    const used = new Set(list.map(x => x.color));
    const color = palette.find(c => !used.has(c)) || palette[0] || '#c9935a';
    return [...list, { key: '', name: '', rate: 1, color, counts_goal: true, notice_away: true, hidden: false, goal_minutes: 60, show_home: false }];
  });
  const saveSubjects = () => {
    setSavingSubjects(true);
    setErrorMsg('');
    axios.put(`${API}/study/subjects`, { token, subjects: subjects.filter(x => x.name.trim()).map(({ _goalDraft, ...x }) => x) })
      .then(res => { setSubjects(res.data.subjects); setSubjectsSaved(JSON.stringify(res.data.subjects)); showToast('subjects saved'); })
      .catch(err => setErrorMsg(`couldn't save the subjects: ${describeError(err)}`))
      .finally(() => setSavingSubjects(false));
  };
  const subjectsChanged = subjects && JSON.stringify(subjects.map(({ _goalDraft, ...x }) => x)) !== subjectsSaved;

  // ---- stickers ----
  const upload = (moment, files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(moment);
    setErrorMsg('');
    const sendOne = (i) => {
      if (i >= list.length) {
        setUploading('');
        loadStickers();
        showToast(list.length === 1 ? 'sticker added' : `${list.length} stickers added`);
        return;
      }
      const form = new FormData();
      form.append('file', list[i]);
      axios.post(`${API}/study/stickers?token=${token}&moment=${moment}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
        .then(() => sendOne(i + 1))
        .catch(err => { setUploading(''); setErrorMsg(`couldn't upload ${list[i].name}: ${describeError(err)}`); loadStickers(); });
    };
    sendOne(0);
  };

  const remove = (id) => {
    axios.delete(`${API}/study/stickers/${id}?token=${token}`)
      .then(() => { setConfirmDelete(''); loadStickers(); })
      .catch(err => setErrorMsg(`couldn't remove it: ${describeError(err)}`));
  };

  // switch a sticker in or out of the study rotation — never deletes it
  const toggleRotation = (st) => {
    const next = st.show_in_rotation === false;
    setStickers(list => list.map(x => (x.id === st.id ? { ...x, show_in_rotation: next } : x)));
    axios.patch(`${API}/study/stickers/${st.id}`, { token, show_in_rotation: next })
      .then(() => showToast(next ? 'back in the rotation' : 'hidden from the rotation'))
      .catch(err => { setErrorMsg(`couldn't change it: ${describeError(err)}`); loadStickers(); });
  };

  // ---- lines ----
  const linesFrom = (text) => (text || '').split('\n').map(l => l.trim()).filter(Boolean);

  const saveLines = (key, text) => {
    setSavingCat(key);
    setErrorMsg('');
    axios.put(`${API}/study/lines/${key}`, { token, lines: linesFrom(text) })
      .then(res => {
        setCategories(cs => cs.map(c => (c.key === key ? { ...c, custom: res.data.custom } : c)));
        setDrafts(d => ({ ...d, [key]: res.data.custom.join('\n') }));
        showToast(res.data.custom.length ? 'saved' : 'back to the built-in lines');
      })
      .catch(err => setErrorMsg(`couldn't save: ${describeError(err)}`))
      .finally(() => setSavingCat(''));
  };

  const suggest = (key) => {
    setSuggesting(key);
    setErrorMsg('');
    axios.post(`${API}/study/lines/${key}/suggest`, { token })
      .then(res => {
        const ideas = res.data.lines || [];
        setDrafts(d => {
          const current = (d[key] || '').trim();
          return { ...d, [key]: current ? `${current}\n${ideas.join('\n')}` : ideas.join('\n') };
        });
        showToast('added his ideas — edit, then save');
      })
      .catch(err => setErrorMsg(`couldn't get ideas: ${describeError(err)}`))
      .finally(() => setSuggesting(''));
  };

  const mineFor = (moment) => stickers.filter(st => st.moment === moment);

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400;500&display=swap" rel="stylesheet" />

      <header style={s.header}>
        <button style={s.back} onClick={() => navigate('/study')} aria-label="back">←</button>
        <div style={s.title}>study settings</div>
      </header>

      <main style={s.main}>
        {errorMsg && <div style={s.error} onClick={() => setErrorMsg('')}><Alert />{errorMsg}</div>}

        {/* READING TIME */}
        <section style={s.section}>
          <div style={s.sectionLabel}>reading time</div>
          <div style={s.card}>
            <div style={s.cardHead}>
              <div style={{ minWidth: 0 }}>
                <div style={s.cardTitle}>Reading Time</div>
                <div style={s.hint}>Show the reading-time interface.</div>
              </div>
              <button
                role="switch"
                aria-checked={!!reading?.visible}
                aria-label="show reading time"
                disabled={!reading || readingSaving}
                onClick={() => setReadingVisible(!reading.visible)}
                style={s.switchTrack(!!reading?.visible)}
              >
                <span style={s.switchKnob(!!reading?.visible)} />
              </button>
            </div>
            <div style={{ ...s.hint, marginTop: '10px' }}>
              every minute you study becomes a minute to read — that keeps happening even while this is off,
              and your balance is never touched.
              {reading && <> you have <span style={{ color: '#c9a57a' }}>{readingHm(reading.balance_seconds)}</span> saved up.</>}
              {reading?.active && <> you're reading right now — turning this off ends that session and saves it.</>}
            </div>
          </div>
        </section>

        {/* SUBJECTS */}
        {subjects && (
          <section style={s.section}>
            <div style={s.sectionLabel}>subjects</div>
            <div style={s.hint}>
              what you're studying, and how much reading time each hour earns. each subject has its own
              daily goal and streak; every subject counts as time together with Caleb.
            </div>
            {subjects.map((x, i) => (
              <div key={x.key || `new-${i}`} style={{ ...s.card, opacity: x.hidden ? 0.55 : 1 }}>
                <div style={s.subjTop}>
                  <input style={s.subjName} value={x.name} placeholder="subject name" aria-label="subject name"
                    onChange={e => editSubject(i, { name: e.target.value })} />
                  <button style={{ ...s.eyeBtn, color: x.hidden ? '#6d5d4e' : '#c9935a' }} onClick={() => editSubject(i, { hidden: !x.hidden })}
                    aria-label={x.hidden ? 'show on the study page' : 'hide from the study page'} title={x.hidden ? 'hidden — tap to show' : 'showing — tap to hide'}>
                    <Eye open={!x.hidden} />
                  </button>
                </div>
                <div style={s.subjRow}>
                  <span style={s.subjLabel}>reading time</span>
                  {[[1, '1 for 1'], [0.75, '¾'], [0.5, '½'], [0.25, '¼']].map(([r, label]) => (
                    <button key={r} style={s.rateChip(x.rate === r)} onClick={() => editSubject(i, { rate: r })}>{label}</button>
                  ))}
                </div>
                <div style={s.subjRow}>
                  <span style={s.subjLabel}>colour</span>
                  {palette.map(c => (
                    <button key={c} onClick={() => editSubject(i, { color: c })} aria-label={`colour ${c}`}
                      style={{ ...s.swatch, background: c, boxShadow: x.color === c ? `0 0 0 2px #0e0b0f, 0 0 0 3.5px ${c}` : 'none' }} />
                  ))}
                </div>
                <div style={s.subjRow}>
                  <span style={s.subjLabel}>daily goal</span>
                  <input style={s.goalInput} inputMode="decimal" aria-label={`daily goal for ${x.name || 'this subject'} in hours`}
                    value={x._goalDraft ?? String(Math.round(((x.goal_minutes || 60) / 60) * 100) / 100)}
                    onChange={e => {
                      const v = e.target.value;
                      const h = parseFloat(v);
                      editSubject(i, { _goalDraft: v, ...(Number.isFinite(h) && h > 0 ? { goal_minutes: Math.max(15, Math.min(720, Math.round(h * 60))) } : {}) });
                    }} />
                  <span style={s.goalUnit}>hours a day</span>
                </div>
                <label style={s.checkRow}>
                  <input type="checkbox" checked={!!x.show_home} onChange={e => editSubject(i, { show_home: e.target.checked })} />
                  show on the homepage
                </label>
                <label style={s.checkRow}>
                  <input type="checkbox" checked={!!x.notice_away} onChange={e => editSubject(i, { notice_away: e.target.checked })} />
                  Caleb notices when you leave the page
                </label>
              </div>
            ))}
            <div style={s.row}>
              <button style={s.smallBtn} onClick={addSubject}>+ add a subject</button>
              <button style={subjectsChanged && !savingSubjects ? s.primaryBtn : s.primaryBtnOff} disabled={!subjectsChanged || savingSubjects} onClick={saveSubjects}>
                {savingSubjects ? 'saving…' : 'save subjects'}
              </button>
            </div>
          </section>
        )}

        {/* STICKERS */}
        <section style={s.section}>
          <div style={s.sectionLabel}>stickers</div>
          <div style={s.hint}>
            upload stickers for each moment. PNG or WebP with a see-through background looks best.
            when you've added your own for a moment, they replace the built-in ones. tap the eye to hide one
            from the rotation without deleting it. the ones marked with a heart are from Caleb — they live
            in From Caleb, so they can only be hidden here.
          </div>

          {(moments.length ? moments : Object.keys(MOMENT_LABELS).map(k => ({ key: k, description: '' }))).map(m => {
            const mine = mineFor(m.key);
            const visible = mine.filter(st => st.show_in_rotation !== false);
            return (
              <div key={m.key} style={s.card}>
                <div style={s.cardHead}>
                  <div>
                    <div style={s.cardTitle}>{MOMENT_LABELS[m.key] || m.key}</div>
                    <div style={s.hint}>{m.description}</div>
                  </div>
                  <button
                    style={uploading === m.key ? s.smallBtnOff : s.smallBtn}
                    disabled={!!uploading}
                    onClick={() => fileRefs.current[m.key]?.click()}
                  >
                    {uploading === m.key ? 'uploading…' : '+ add'}
                  </button>
                  <input
                    ref={el => { fileRefs.current[m.key] = el; }}
                    type="file" accept="image/*" multiple style={{ display: 'none' }}
                    onChange={e => { upload(m.key, e.target.files); e.target.value = ''; }}
                  />
                </div>

                <div style={s.grid}>
                  {mine.map(st => {
                    const hidden = st.show_in_rotation === false;
                    const fromCaleb = st.source === 'caleb_gift' || st.is_keepsake;
                    return (
                      <div key={st.id} style={s.thumbWrap}>
                        <img src={st.url} alt="" style={{ ...s.thumb, opacity: hidden ? 0.3 : 1 }} />
                        {fromCaleb && (
                          <span style={s.fromTag} title="from Caleb — kept in From Caleb"><Heart /></span>
                        )}
                        <button
                          style={{ ...s.eye, color: hidden ? '#6d5d4e' : '#c9935a' }}
                          onClick={() => toggleRotation(st)}
                          aria-label={hidden ? 'show in rotation' : 'hide from rotation'}
                          title={hidden ? 'hidden — tap to show' : 'in the rotation — tap to hide'}
                        >
                          <Eye open={!hidden} />
                        </button>
                        {!fromCaleb && (confirmDelete === st.id ? (
                          <div style={s.confirm}>
                            <button style={s.confirmYes} onClick={() => remove(st.id)}>delete</button>
                            <button style={s.confirmNo} onClick={() => setConfirmDelete('')}>keep</button>
                          </div>
                        ) : (
                          <button style={s.x} onClick={() => setConfirmDelete(st.id)} aria-label="delete sticker">×</button>
                        ))}
                      </div>
                    );
                  })}
                  {visible.length === 0 && (BUILT_IN[m.key] || []).map(src => (
                    <div key={src} style={{ ...s.thumbWrap, opacity: 0.45 }} title="built-in">
                      <img src={src} alt="" style={s.thumb} />
                    </div>
                  ))}
                </div>
                {visible.length === 0 && (
                  <div style={s.builtInNote}>
                    {mine.length ? 'all yours are hidden, so the built-in ones show' : 'using the built-in stickers'}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* MESSAGES */}
        <section style={s.section}>
          <div style={s.sectionLabel}>what he says</div>
          <div style={s.hint}>
            one message per line. paste a whole list (from ChatGPT or anywhere) and save. each time, he picks one
            at random. leave a box empty to use the built-in lines.
          </div>

          {categories.map(c => {
            const open = openCat === c.key;
            const count = c.custom.length;
            const draft = drafts[c.key] ?? '';
            const changed = draft.trim() !== c.custom.join('\n').trim();
            return (
              <div key={c.key} style={s.card}>
                <button style={s.catHead} onClick={() => setOpenCat(open ? '' : c.key)}>
                  <div style={{ minWidth: 0, textAlign: 'left' }}>
                    <div style={s.cardTitle}>{c.label}</div>
                    <div style={s.hint}>{c.when}</div>
                  </div>
                  <span style={s.count}>{count ? `${count} yours` : 'built-in'} {open ? '▴' : '▾'}</span>
                </button>

                {open && (
                  <div style={s.catBody}>
                    <textarea
                      style={s.textarea}
                      value={draft}
                      onChange={e => setDrafts(d => ({ ...d, [c.key]: e.target.value }))}
                      placeholder={c.defaults.join('\n')}
                      rows={Math.min(12, Math.max(4, linesFrom(draft).length + 2))}
                    />
                    <div style={s.lineCount}>{linesFrom(draft).length} message{linesFrom(draft).length === 1 ? '' : 's'}</div>
                    <div style={s.row}>
                      <button
                        style={changed && savingCat !== c.key ? s.primaryBtn : s.primaryBtnOff}
                        disabled={!changed || savingCat === c.key}
                        onClick={() => saveLines(c.key, draft)}
                      >
                        {savingCat === c.key ? 'saving…' : 'save'}
                      </button>
                      <button style={s.smallBtn} disabled={suggesting === c.key} onClick={() => suggest(c.key)}>
                        {suggesting === c.key ? 'thinking…' : <span style={s.iconLabel}><Sparkle /> ideas from caleb</span>}
                      </button>
                      {count > 0 && (
                        <button style={s.quietBtn} onClick={() => saveLines(c.key, '')}>use built-in</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </main>

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  // its own scrolling area, like your other pages (the site stops the page itself from scrolling)
  page: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#0e0b0f', color: '#e8ddd0', fontFamily: "'DM Sans', sans-serif", fontWeight: 300 },
  header: {
    position: 'sticky', top: 0, zIndex: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', gap: '12px',
    background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', borderBottom: '1px solid rgba(201,147,90,0.06)',
  },
  back: { width: '36px', height: '36px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.15)', background: 'transparent', color: '#c9935a', fontSize: '16px', cursor: 'pointer', flexShrink: 0 },
  title: { flex: 1, fontFamily: "'Cormorant Garamond', serif", fontSize: '21px', fontStyle: 'italic' },
  main: { maxWidth: '640px', margin: '0 auto', padding: '20px 20px 90px', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '30px' },
  section: { display: 'flex', flexDirection: 'column', gap: '10px' },
  sectionLabel: { fontSize: '10px', color: '#6d5d4e', letterSpacing: '0.12em', textTransform: 'uppercase', paddingLeft: '2px' },
  hint: { fontSize: '12px', color: '#7a6a5a', lineHeight: 1.6 },
  card: { background: '#1e1825', border: '1px solid rgba(201,147,90,0.08)', borderRadius: '18px', padding: '14px 16px' },
  cardHead: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' },
  cardTitle: { fontSize: '14.5px', color: '#e8ddd0', marginBottom: '2px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))', gap: '10px', marginTop: '12px' },
  thumbWrap: { position: 'relative', aspectRatio: '1 / 1', background: '#120f18', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  thumb: { maxWidth: '88%', maxHeight: '88%', objectFit: 'contain' },
  x: { position: 'absolute', top: '4px', right: '4px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(14,11,15,0.8)', color: '#e8ddd0', fontSize: '14px', lineHeight: 1, cursor: 'pointer', padding: 0 },
  eye: { position: 'absolute', bottom: '4px', left: '4px', width: '24px', height: '24px', borderRadius: '50%', border: 'none', background: 'rgba(14,11,15,0.8)', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  fromTag: { position: 'absolute', top: '5px', left: '6px', color: '#d9a3b8', display: 'flex' },
  iconLabel: { display: 'inline-flex', alignItems: 'center', gap: '6px' },
  confirm: { position: 'absolute', inset: 0, background: 'rgba(14,11,15,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px' },
  confirmYes: { border: 'none', background: '#b04a4a', color: '#f4e6e6', borderRadius: '10px', padding: '4px 10px', fontSize: '11.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  confirmNo: { border: 'none', background: 'transparent', color: '#8a7466', fontSize: '11.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  builtInNote: { fontSize: '11px', color: '#6d5d4e', marginTop: '8px' },
  catHead: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'inherit', fontFamily: 'inherit' },
  count: { fontSize: '11.5px', color: '#c9a57a', whiteSpace: 'nowrap', flexShrink: 0 },
  catBody: { marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
  textarea: { width: '100%', boxSizing: 'border-box', background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.15)', borderRadius: '12px', padding: '12px 14px', color: '#e8ddd0', fontSize: '14px', lineHeight: 1.7, fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'vertical' },
  lineCount: { fontSize: '11px', color: '#6d5d4e', textAlign: 'right' },
  row: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  primaryBtn: { padding: '9px 18px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8a5f35, #c9935a)', color: '#0e0b0f', fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  primaryBtnOff: { padding: '9px 18px', borderRadius: '12px', border: 'none', background: 'rgba(201,147,90,0.15)', color: 'rgba(201,147,90,0.45)', fontSize: '13px', cursor: 'default', fontFamily: "'DM Sans', sans-serif" },
  smallBtn: { padding: '8px 14px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.22)', background: 'transparent', color: '#c9935a', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap', flexShrink: 0 },
  smallBtnOff: { padding: '8px 14px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.1)', background: 'transparent', color: 'rgba(201,147,90,0.45)', fontSize: '12.5px', fontFamily: "'DM Sans', sans-serif", whiteSpace: 'nowrap', flexShrink: 0 },
  quietBtn: { padding: '8px 10px', border: 'none', background: 'transparent', color: '#6d5d4e', fontSize: '12.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" },
  error: { background: 'rgba(180,60,60,0.12)', border: '1px solid rgba(220,100,100,0.3)', borderRadius: '12px', padding: '10px 14px', fontSize: '12px', color: '#e0a0a0', cursor: 'pointer' },
  switchTrack: (on) => ({
    position: 'relative', width: '46px', height: '26px', borderRadius: '13px', flexShrink: 0, cursor: 'pointer', padding: 0,
    border: on ? 'none' : '1px solid rgba(201,147,90,0.25)',
    background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', transition: 'background .25s ease',
  }),
  switchKnob: (on) => ({
    position: 'absolute', top: '3px', left: on ? '23px' : '3px', width: '20px', height: '20px', borderRadius: '50%',
    background: on ? '#0e0b0f' : '#6d5d4e', transition: 'left .25s ease',
  }),
  subjTop: { display: 'flex', alignItems: 'center', gap: '10px' },
  subjName: { flex: 1, minWidth: 0, background: 'transparent', border: 'none', borderBottom: '1px solid rgba(201,147,90,0.15)', color: '#e8ddd0', fontSize: '15px', padding: '4px 0', fontFamily: "'DM Sans', sans-serif", outline: 'none' },
  eyeBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' },
  subjRow: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '12px' },
  subjLabel: { fontSize: '10.5px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#6d5d4e', width: '96px' },
  rateChip: (on) => ({ padding: '5px 12px', borderRadius: '14px', fontSize: '12px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", border: on ? 'none' : '1px solid rgba(201,147,90,0.18)', background: on ? 'linear-gradient(135deg, #8a5f35, #c9935a)' : 'transparent', color: on ? '#0e0b0f' : '#c9a57a' }),
  swatch: { width: '22px', height: '22px', borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0 },
  goalInput: { width: '58px', background: '#120f18', border: '1px solid rgba(201,147,90,0.18)', borderRadius: '9px', padding: '6px 9px', color: '#e8ddd0', fontSize: '13px', fontFamily: "'DM Sans', sans-serif", outline: 'none', textAlign: 'center' },
  goalUnit: { fontSize: '12px', color: '#8a7466' },
  checkRow: { display: 'flex', alignItems: 'center', gap: '9px', marginTop: '10px', fontSize: '12.5px', color: '#cdbfae', cursor: 'pointer' },
  toast: { position: 'fixed', bottom: '32px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(30,24,37,0.96)', border: '1px solid rgba(201,147,90,0.3)', color: '#c9935a', padding: '10px 20px', borderRadius: '20px', fontSize: '13px', zIndex: 300, maxWidth: 'calc(100vw - 32px)', textAlign: 'center' },
};
